import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ACCOUNT_SELECTION_STORAGE_KEY } from '@core/constants/auth';
import { applyAccountRuntime } from '@core/utils/accountRuntime';
import { setRuntimeApiKey } from '@core/store/runtimeApiStore';
import { useUiStore } from '@core/store/uiStore';
import { useStoreContextStore } from '@core/store/storeContextStore';

const empty = {
  primaryAccount: null,
  linkedAccounts: [],
  availableAccounts: [],
  accountChannelId: '',
  activeAccountId: '',
  accountSelectorOpen: false,
  hasLinkedAccounts: false,
};

function getAccountId(account) {
  return String(
    account?.id || account?.accountId || account?.account_details?.account_id || '',
  ).trim();
}

function getAccountName(account) {
  return (
    String(
      account?.business_name ||
        account?.brand_name ||
        account?.name ||
        account?.account_details?.business_name ||
        account?.account_details?.brand_name ||
        account?.account_details?.name ||
        account?.email ||
        account?.account_details?.email ||
        account?.id ||
        '',
    ).trim() || 'Account'
  );
}

function getAccountSubtitle(account) {
  return (
    String(
      account?.email ||
        account?.account_details?.email ||
        account?.business_name ||
        account?.account_details?.business_name ||
        '',
    ).trim() || ''
  );
}

function pickApiKey(source = {}) {
  return (
    source?.apiKey ||
    source?.fullApiKey ||
    source?.FullApiKey ||
    source?.full_api_key ||
    source?.api_keys?.[0]?.FullApiKey ||
    source?.api_keys?.[0]?.fullApiKey ||
    source?.api_keys?.[0]?.full_api_key ||
    source?.api_keys?.[0]?.apiKey ||
    null
  );
}

function normalizeAccount(raw, { isPrimary = false } = {}) {
  const details = raw?.account_details || raw || {};
  const id = getAccountId(raw) || getAccountId(details);
  if (!id) return null;

  const apiKey = pickApiKey(raw) || pickApiKey(details);
  return {
    id,
    apiKey,
    isPrimary: Boolean(isPrimary || details.is_primary_account),
    accountDetails: details,
    name: getAccountName(details),
    subtitle: getAccountSubtitle(details),
    email: String(details.email || '').trim(),
  };
}

export function normalizeResolvedAccounts(resolved = {}) {
  const primaryAccount = normalizeAccount(resolved, { isPrimary: true });
  const linkedAccounts = Array.isArray(resolved?.linked_accounts)
    ? resolved.linked_accounts.map((account) => normalizeAccount(account)).filter(Boolean)
    : [];
  const availableAccounts = [primaryAccount, ...linkedAccounts].filter(Boolean);

  const deduped = [];
  const seen = new Set();
  for (const account of availableAccounts) {
    if (seen.has(account.id)) continue;
    seen.add(account.id);
    deduped.push(account);
  }

  return {
    primaryAccount: primaryAccount || null,
    linkedAccounts,
    availableAccounts: deduped,
    accountChannelId: String(resolved?.channel_id || resolved?.channelId || '').trim(),
    hasLinkedAccounts: Boolean(resolved && resolved?.linked_accounts?.length > 0),
  };
}

export const useAccountSelectionStore = create(
  persist(
    (set, get) => ({
      ...empty,

      setResolvedAccounts: ({
        primaryAccount,
        linkedAccounts,
        availableAccounts,
        accountChannelId,
        hasLinkedAccounts,
      }) =>
        set((state) => {
          const nextAccounts = Array.isArray(availableAccounts)
            ? availableAccounts
            : [primaryAccount, ...(linkedAccounts || [])].filter(Boolean);
          const current =
            nextAccounts.find((account) => account.id === state.activeAccountId) || null;
          return {
            ...state,
            primaryAccount: primaryAccount || null,
            linkedAccounts: Array.isArray(linkedAccounts) ? linkedAccounts : [],
            availableAccounts: nextAccounts,
            accountChannelId: String(accountChannelId || state.accountChannelId || '').trim(),
            activeAccountId: current?.id || '',
            hasLinkedAccounts:
              hasLinkedAccounts !== undefined
                ? Boolean(hasLinkedAccounts)
                : state.hasLinkedAccounts,
          };
        }),

      openAccountSelector: () => set({ accountSelectorOpen: true }),

      closeAccountSelector: () => set({ accountSelectorOpen: false }),

      setActiveAccount: (accountOrId, { resetLocation = false } = {}) => {
        const accountId =
          typeof accountOrId === 'string' ? String(accountOrId).trim() : getAccountId(accountOrId);
        if (!accountId) return null;

        const state = get();
        const nextAccount =
          (typeof accountOrId === 'string'
            ? state.availableAccounts.find((account) => account.id === accountId)
            : accountOrId) ||
          state.availableAccounts.find((account) => account.id === accountId) ||
          null;

        if (!nextAccount) return null;

        const previousAccountId = state.activeAccountId || '';
        const isSwitchingAccounts = previousAccountId && previousAccountId !== accountId;
        const nextApiKey = String(nextAccount.apiKey || '').trim() || null;

        setRuntimeApiKey(nextApiKey);
        applyAccountRuntime(nextAccount);
        const shouldResetLocation =
          isSwitchingAccounts || (resetLocation && previousAccountId !== accountId);

        useStoreContextStore.getState().hydrate({
          accountId,
          channelId: state.accountChannelId || useStoreContextStore.getState().channelId || '',
          ...(shouldResetLocation
            ? {
                locationId: '',
                brandId: '',
                brandName: '',
                companyId: '',
                companyName: '',
                customerLocation: null,
              }
            : {}),
        });

        set({
          activeAccountId: accountId,
        });

        return nextAccount;
      },

      clearSelection: () =>
        set({
          ...empty,
        }),

      selectAccount: (accountOrId) => {
        const selected = get().setActiveAccount(accountOrId, { resetLocation: true });
        if (!selected) return null;

        set({ accountSelectorOpen: false });
        useUiStore.getState().openStoreSelector();
        return selected;
      },
    }),
    {
      name: ACCOUNT_SELECTION_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeAccountId: state.activeAccountId,
      }),
    },
  ),
);

export function getSelectedAccountId() {
  return useAccountSelectionStore.getState().activeAccountId;
}

export function getActiveAccount() {
  const state = useAccountSelectionStore.getState();
  return state.availableAccounts.find((account) => account.id === state.activeAccountId) || null;
}

export { getAccountId, getAccountName, getAccountSubtitle, normalizeAccount, pickApiKey };
